import { GraphQLClient, gql } from "graphql-request";
import config from "./config";

export interface CharityDonation {
  name: string;
  imageUrl?: string;
  about: string[];
  sku: string;
  amounts: string[];
  allowCustomAmount: boolean;
}

type MetaobjectField = {
  key: string;
  value?: string | null;
  reference?: {
    __typename: string;
    url?: string | null;
    sku?: string | null;
    image?: { url?: string | null } | null;
  } | null;
};

const charityDonationsQuery = gql`
  query CharityDonations {
    metaobjects(type: "charity_donations", first: 50) {
      nodes {
        fields {
          key
          value
          reference {
            __typename
            ... on GenericFile {
              url
            }
            ... on MediaImage {
              image {
                url
              }
            }
            ... on ProductVariant {
              sku
            }
          }
        }
      }
    }
  }
`;

function getField(fields: MetaobjectField[], key: string) {
  return fields.find((field) => field.key === key);
}

function parseStringList(value?: string | null) {
  if (!value) return [];

  try {
    const items = JSON.parse(value);
    return Array.isArray(items) ? items.filter((item): item is string => typeof item === "string") : [];
  } catch {
    return [];
  }
}

function parseDonationAmounts(value?: string | null) {
  if (!value) return [];

  try {
    const amounts = JSON.parse(value);
    if (Array.isArray(amounts)) {
      return amounts
        .map((amount) => Number(String(amount).replace("$", "")))
        .filter((amount) => Number.isFinite(amount) && amount > 0)
        .map((amount) => `$${amount}`);
    }
  } catch {
    // Support a comma-separated text field too.
  }

  return value
    .split(",")
    .map((amount) => Number(amount.trim().replace("$", "")))
    .filter((amount) => Number.isFinite(amount) && amount > 0)
    .map((amount) => `$${amount}`);
}

function richTextToParagraphs(value?: string | null) {
  if (!value) return [];

  try {
    const collectText = (node: unknown): string => {
      if (!node || typeof node !== "object") return "";
      const { value, children } = node as { value?: unknown; children?: unknown };
      return `${typeof value === "string" ? value : ""}${
        Array.isArray(children) ? children.map(collectText).join("") : ""
      }`;
    };
    const root = JSON.parse(value) as { children?: unknown[] };

    return (root.children ?? [])
      .map(collectText)
      .map((paragraph) => paragraph.trim())
      .filter(Boolean);
  } catch {
    return [value.trim()].filter(Boolean);
  }
}

export async function getCharityDonation(): Promise<CharityDonation | null> {
  if (!config.shopifyStorefrontUrl || !config.shopifyAccessToken) return null;

  const client = new GraphQLClient(config.shopifyStorefrontUrl, {
    headers: {
      "X-Shopify-Storefront-Access-Token": config.shopifyAccessToken,
    },
  });

  try {
    const countryCodes = config.countryCode === "GB" ? ["GB", "UK"] : [config.countryCode];
    const environment = config.environment.trim().toLowerCase();
    const response = await client.request<{
      metaobjects: { nodes: { fields: MetaobjectField[] }[] };
    }>(charityDonationsQuery);
    const fields = response.metaobjects.nodes
      .map((metaobject) => metaobject.fields)
      .find((entryFields) =>
        parseStringList(getField(entryFields, "environments")?.value)
          .some((entryEnvironment) => entryEnvironment.trim().toLowerCase() === environment) &&
        parseStringList(getField(entryFields, "markets")?.value)
          .some((market) => countryCodes.includes(market.trim().toUpperCase()))
      );

    if (!fields) return null;

    const name = getField(fields, "charity_name")?.value?.trim();
    const sku = getField(fields, "donation_sku")?.reference?.sku
      ?? getField(fields, "donation_sku")?.value?.trim();
    if (!name || !sku) return null;

    const image = getField(fields, "charity_image");
    return {
      name,
      imageUrl: image?.reference?.image?.url ?? image?.reference?.url ?? image?.value ?? undefined,
      about: richTextToParagraphs(getField(fields, "about_charity")?.value),
      sku,
      amounts: parseDonationAmounts(getField(fields, "donation_amounts")?.value),
      allowCustomAmount: getField(fields, "allow_custom_amount")?.value?.trim().toLowerCase() === "true",
    };
  } catch (error) {
    console.error("Unable to load charity donations", error);
    return null;
  }
}
